import { Planet, PrimevalLegacy } from '@apeiron/planet'
import { styled } from '@mui/material'
import { CoreStateProgress } from '@src/components/planet/detail/planetAboutSection/CoreStateProgress'
import { PlanetAboutItem } from '@src/components/planet/detail/planetAboutSection/PlanetAboutItem'
import { SectionContent } from '@src/components/planet/detail/SectionContent'
import ApeironOwnerView from '@src/components/share/apeiron/ApeironOwnerView'
import { ROUTE } from '@src/constants/route'
import { usePrimevalLegacyContext } from '@src/contexts/planet/PrimevalLegacyContext'
import React from 'react'
import { useTranslation } from 'react-i18next'

const StyledSectionContent = styled(SectionContent)`
  display: flex;
  flex-direction: column;
`

const ItemContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 36px;
  grid-row-gap: 13px;
  justify-content: space-between;
`

const StyledCoreStateProgress = styled(CoreStateProgress)`
  width: 300px;
  margin-bottom: 18px;
`

const StyledApeironOwnerView = styled(ApeironOwnerView)`
  margin-top: 10px;
`

export const CoreAboutSection: React.FC<CoreAboutSectionProps> = props => {
  const { className, planet } = props

  const { t } = useTranslation()

  function getPrimevalLegacyToolTipTitle(
    primevalLegacyData: PrimevalLegacy
  ): string {
    const elementValues = []

    if (primevalLegacyData.fire !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.fire,
          element: t('element.fire')
        })
      )
    }

    if (primevalLegacyData.water !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.water,
          element: t('element.water')
        })
      )
    }

    if (primevalLegacyData.air !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.air,
          element: t('element.air')
        })
      )
    }

    if (primevalLegacyData.earth !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.earth,
          element: t('element.earth')
        })
      )
    }

    return t('planet.detail.about.item.tool_tip_title.primeval_legacy', {
      values: elementValues.join(', ')
    })
  }

  const primevalLegacyContext = usePrimevalLegacyContext()

  const primevalLegacy = planet.primevalLegacy
    ? primevalLegacyContext.getPrimevalLegacy(planet.primevalLegacy)
    : undefined

  const primevalLegacyName = primevalLegacy
    ? primevalLegacy.name
    : t('planet.detail.about.item.value.primeval_legacy.unknown', {
        primevalLegacyId: planet.primevalLegacy
      })

  const primevalLegacyToolTipTitle = primevalLegacy
    ? getPrimevalLegacyToolTipTitle(primevalLegacy)
    : undefined

  const now = new Date()

  const { planetCreateTime: morphStartAt, bornAt: morphEndAt } = planet

  return (
    <StyledSectionContent className={className}>
      {morphStartAt && morphEndAt && (
        <StyledCoreStateProgress
          startAt={morphStartAt}
          endAt={morphEndAt}
          now={now}
        />
      )}
      <ItemContainer>
        <PlanetAboutItem
          title={t('planet.detail.about.item.title.planet_type')}
          value={planet.planetType}
          isPlanetElement
        />
        {primevalLegacy && (
          <PlanetAboutItem
            title={t('planet.detail.about.item.title.primeval_legacy')}
            value={primevalLegacyName}
            toolTipTitle={primevalLegacyToolTipTitle}
          />
        )}
      </ItemContainer>
      {planet.owner && (
        <StyledApeironOwnerView
          owner={planet.owner}
          route={ROUTE.OTHER_INVENTORY_PLANET_LIST}
        />
      )}
    </StyledSectionContent>
  )
}

export type CoreAboutSectionProps = {
  className?: string
  planet: Planet
}
